import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { SurveyPath, CardQuestion } from '../../types';
import { normalizeTriggerValues } from '../../utils/pathTriggerValues';
import { TriggerStep } from './TriggerStep';

interface PathWizardProps {
  open: boolean;
  onClose: () => void;
  onSave: (path: Omit<SurveyPath, 'id'>) => void;
  questions: CardQuestion[];
  existingPaths?: SurveyPath[];
  existingPath?: SurveyPath;
  triggerQuestionId?: string;
  initialTriggerValues?: string[];
}

export function PathWizard({
  open,
  onClose,
  onSave,
  questions,
  existingPaths = [],
  existingPath,
  triggerQuestionId: initialTriggerQuestionId,
  initialTriggerValues,
}: PathWizardProps) {
  const [triggerQuestionId, setTriggerQuestionId] = useState<string>(
    existingPath?.trigger.questionId || initialTriggerQuestionId || ''
  );
  const [triggerValues, setTriggerValues] = useState<string[]>(
    normalizeTriggerValues(existingPath?.trigger.values).length > 0
      ? normalizeTriggerValues(existingPath?.trigger.values)
      : initialTriggerValues || []
  );
  const [pathQuestions, setPathQuestions] = useState<CardQuestion[]>([]);
  const [pathName, setPathName] = useState<string>(existingPath?.name || '');

  const triggerValuesRef = useRef(triggerValues);
  const pathNameRef = useRef(pathName);
  const triggerQuestionIdRef = useRef(triggerQuestionId);
  const pathQuestionsRef = useRef(pathQuestions);
  triggerValuesRef.current = triggerValues;
  pathNameRef.current = pathName;
  triggerQuestionIdRef.current = triggerQuestionId;
  pathQuestionsRef.current = pathQuestions;

  const handleCreate = () => {
    const latestTriggerValues = [...triggerValuesRef.current];
    const latestPathQuestions = pathQuestionsRef.current;
    const latestPathName = pathNameRef.current;
    const latestTriggerQuestionId = triggerQuestionIdRef.current;

    onSave({
      name: latestPathName || `${latestTriggerValues.join(', ')} Path`,
      trigger: {
        questionId: latestTriggerQuestionId,
        values: latestTriggerValues,
        label: latestTriggerValues[0] ?? latestTriggerValues.join(', '),
      },
      questionIds: latestPathQuestions.map((q) => q.id),
      afterPath: 'continue',
    });
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setTriggerQuestionId(existingPath?.trigger.questionId || initialTriggerQuestionId || '');
    setTriggerValues(
      normalizeTriggerValues(existingPath?.trigger.values).length > 0
        ? normalizeTriggerValues(existingPath?.trigger.values)
        : initialTriggerValues || []
    );
    setPathQuestions([]);
    setPathName(existingPath?.name || '');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const openRef = useRef(open);
  const didInitForOpenRef = useRef(false);
  if (open && !openRef.current) {
    openRef.current = true;
    didInitForOpenRef.current = false;
  }
  if (!open) {
    openRef.current = false;
    didInitForOpenRef.current = false;
  }

  useEffect(() => {
    if (!open) return;
    if (didInitForOpenRef.current) return;
    didInitForOpenRef.current = true;

    if (existingPath) {
      const existingPathQuestions = questions.filter((q) =>
        existingPath.questionIds.includes(q.id)
      );
      setPathQuestions(existingPathQuestions);
      setTriggerQuestionId(existingPath.trigger.questionId);
      setTriggerValues(normalizeTriggerValues(existingPath.trigger.values));
      setPathName(existingPath.name);
    } else {
      setPathQuestions([]);
      setTriggerQuestionId(initialTriggerQuestionId || '');
      setTriggerValues(initialTriggerValues ?? []);
      setPathName(initialTriggerValues?.length ? `${initialTriggerValues.join(', ')} Path` : '');
    }
  }, [open, existingPath, questions, initialTriggerQuestionId, initialTriggerValues]);

  const previousSuggestedRef = useRef<string>('');
  useEffect(() => {
    if (!open || existingPath) return;
    const suggested = triggerValues.length ? `${triggerValues.join(', ')} Path` : '';
    if (suggested && (pathName === '' || pathName === previousSuggestedRef.current)) {
      setPathName(suggested);
    }
    previousSuggestedRef.current = suggested;
  }, [triggerValues, open, existingPath, pathName]);

  const canCreate = () =>
    triggerQuestionId !== '' && triggerValues.length > 0;

  const usedOptionValues = (existingPaths || [])
    .filter(
      (p) =>
        p.trigger.questionId === triggerQuestionId &&
        (existingPath ? p.id !== existingPath.id : true)
    )
    .flatMap((p) => normalizeTriggerValues(p.trigger.values));
  const usedSet = new Set(usedOptionValues);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-lg font-semibold">✨ Create Survey Path</span>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-[280px] pt-1">
          <TriggerStep
            questions={questions}
            selectedQuestionId={triggerQuestionId}
            selectedValues={triggerValues}
            onValuesChange={setTriggerValues}
            onPathNameChange={setPathName}
            pathName={pathName}
            usedOptionValues={Array.from(usedSet)}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate()}
          >
            Create Path
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
